import { useState, useEffect, useMemo } from 'react';
import type { Position, Asset } from '../types/mexc';
import { TrendingUp, TrendingDown, DollarSign, Hash, AlertCircle, Loader2 } from 'lucide-react';
import { getMexcApiClient } from '../services/mexcApi';
import { useCurrentPrices } from '../hooks/useCurrentPrices';
import { useAvailableSymbols } from '../hooks/useMexcApi';
import { useQueryClient } from '@tanstack/react-query';

interface OrderPanelProps {
  selectedSymbol: string | null;
  positions: Position[];
  assets: Asset[];
  onSymbolSelect: (symbol: string) => void;
}

export function OrderPanel({ selectedSymbol, positions: _positions, assets, onSymbolSelect }: OrderPanelProps) {
  // State management
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [side, setSide] = useState<'long' | 'short'>('long');
  const [amountMode, setAmountMode] = useState<'usd' | 'qty'>('usd');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [leverage, setLeverage] = useState(10);
  const [percentage, setPercentage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const [tickerUpdateTrigger, setTickerUpdateTrigger] = useState(0);
  const [contractStatus, setContractStatus] = useState<'valid' | 'invalid' | 'loading'>('valid');
  const [contractError, setContractError] = useState<string | null>(null);

  // Get React Query client for manual data fetching
  const queryClient = useQueryClient();

  // Fetch available symbols for trading
  const { data: availableSymbols = [], isLoading: isLoadingSymbols } = useAvailableSymbols();

  // Get current price for selected symbol
  const { getCurrentPrice } = useCurrentPrices();
  const priceData = selectedSymbol ? getCurrentPrice(selectedSymbol) : null;
  const currentPrice = priceData?.price || null;

  // Debug logging for price data
  useEffect(() => {
    console.log('🔍 Price Debug:', {
      selectedSymbol,
      priceData,
      currentPrice,
      tickerUpdateTrigger
    });
  }, [selectedSymbol, priceData, currentPrice, tickerUpdateTrigger]);

  // Calculate available balance
  const availableBalance = useMemo(() => {
    const usdtAsset = assets.find(a => a.currency === 'USDT');
    return usdtAsset?.availableBalance || 0;
  }, [assets]);

  // Calculate max tradeable amount considering leverage
  const maxTradeableAmount = useMemo(() => {
    return availableBalance * leverage;
  }, [availableBalance, leverage]);

  // Fetch ticker data when symbol is selected (to ensure price is available)
  useEffect(() => {
    if (!selectedSymbol) return;

    const fetchTickerData = async () => {
      setIsFetchingPrice(true);
      try {
        const mexcApi = getMexcApiClient();
        const ticker = await mexcApi.getTicker(selectedSymbol);

        // Store in React Query cache so useCurrentPrices can find it
        queryClient.setQueryData(['ticker', selectedSymbol], ticker);

        console.log(`✅ Fetched ticker for ${selectedSymbol}:`, ticker);

        // Trigger a re-render to update currentPrice
        setTickerUpdateTrigger(prev => prev + 1);
      } catch (err) {
        console.error(`❌ Failed to fetch ticker for ${selectedSymbol}:`, err);
      } finally {
        setIsFetchingPrice(false);
      }
    };

    fetchTickerData();
  }, [selectedSymbol, queryClient]);

  // Update price when switching to limit order or when current price changes
  useEffect(() => {
    if (orderType === 'limit' && currentPrice && !price) {
      setPrice(currentPrice.toString());
    }
  }, [orderType, currentPrice, price]);

  // Convert between USD and QTY
  const convertAmount = useMemo(() => {
    if (!currentPrice || !amount) return { usd: 0, qty: 0 };

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return { usd: 0, qty: 0 };

    if (amountMode === 'usd') {
      return {
        usd: numAmount,
        qty: numAmount / currentPrice
      };
    } else {
      return {
        usd: numAmount * currentPrice,
        qty: numAmount
      };
    }
  }, [amount, currentPrice, amountMode]);

  // Update amount based on percentage slider
  const handlePercentageChange = (newPercentage: number) => {
    setPercentage(newPercentage);
    const targetAmount = maxTradeableAmount * (newPercentage / 100);
    
    if (amountMode === 'usd') {
      setAmount(targetAmount.toFixed(2));
    } else if (currentPrice) {
      setAmount((targetAmount / currentPrice).toFixed(6));
    }
  };

  // Handle amount input change
  const handleAmountChange = (value: string) => {
    setAmount(value);
    
    // Update percentage based on amount
    if (value && currentPrice && maxTradeableAmount > 0) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const usdValue = amountMode === 'usd' ? numValue : numValue * currentPrice;
        const newPercentage = Math.min(100, (usdValue / maxTradeableAmount) * 100);
        setPercentage(newPercentage);
      }
    }
  };

  // Calculate order details
  const orderDetails = useMemo(() => {
    if (!currentPrice || !amount) return null;

    const orderPrice = orderType === 'market' ? currentPrice : parseFloat(price) || currentPrice;
    const qty = convertAmount.qty;
    const notionalValue = convertAmount.usd;
    const requiredMargin = notionalValue / leverage;
    
    // Estimate liquidation price
    const maintenanceMarginRate = 0.004; // 0.4% typical for futures
    const liquidationPrice = side === 'long'
      ? orderPrice * (1 - (1 / leverage) + maintenanceMarginRate)
      : orderPrice * (1 + (1 / leverage) - maintenanceMarginRate);

    return {
      qty,
      notionalValue,
      requiredMargin,
      liquidationPrice,
      orderPrice
    };
  }, [currentPrice, amount, price, orderType, leverage, side, convertAmount]);

  // Form validation
  const isFormValid = useMemo(() => {
    console.log('🔍 Form Validation Debug:', {
      selectedSymbol,
      amount,
      isSubmitting,
      orderType,
      price,
      orderDetails,
      availableBalance,
      currentPrice
    });

    // Stricter check: Block if submitting to prevent race conditions
    if (!selectedSymbol || !amount || isSubmitting) {
      console.log('❌ Validation failed: Missing symbol, amount, or submitting');
      return false;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      console.log('❌ Validation failed: Invalid amount');
      return false;
    }

    if (orderType === 'limit' && (!price || parseFloat(price) <= 0)) {
      console.log('❌ Validation failed: Invalid limit price');
      return false;
    }

    if (orderDetails && orderDetails.requiredMargin > availableBalance) {
      console.log('❌ Validation failed: Insufficient balance');
      return false;
    }

    console.log('✅ Form validation passed!');
    return true;
  }, [selectedSymbol, amount, price, orderType, isSubmitting, orderDetails, availableBalance, currentPrice]);

  // Pre-submission contract validation
  useEffect(() => {
    if (selectedSymbol && isFormValid) {
      const validateContract = async () => {
        setContractStatus('loading');
        setContractError(null);
        try {
          const mexcApi = getMexcApiClient();
          const contractInfo = await mexcApi.getContractInfo(selectedSymbol);
          const info = Array.isArray(contractInfo) ? contractInfo[0] : contractInfo;
          if (!info || info.state !== 1 || !info.apiAllowed) {
            const msg = `Contract ${selectedSymbol} does not allow trading (state: ${info?.state || 'unknown'})`;
            setContractStatus('invalid');
            setContractError(msg);
            return;
          }
          setContractStatus('valid');
        } catch (err: any) {
          const msg = `Failed to validate contract: ${err.message}`;
          setContractStatus('invalid');
          setContractError(msg);
        }
      };
      validateContract();
    } else {
      setContractStatus('valid');
      setContractError(null);
    }
  }, [selectedSymbol, isFormValid]);

  const finalFormValid = isFormValid && contractStatus === 'valid' && contractError === null;

  // Handle order submission
  const handlePlaceOrder = async () => {
    if (!finalFormValid || !selectedSymbol || !orderDetails || contractStatus !== 'valid') {
      setError('Form or contract validation failed. Please check inputs and symbol status.');
      return;
    }

    if (contractError) {
      setError(contractError);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const apiClient = getMexcApiClient();
      
      // Map side to MEXC API values
      // 1: open long, 2: close short, 3: open short, 4: close long
      const apiSide = side === 'long' ? 1 : 3;
      
      // Map order type to MEXC API values
      // 1: limit, 5: market
      const apiOrderType = orderType === 'limit' ? 1 : 5;

      const orderRequest = {
        symbol: selectedSymbol,
        vol: orderDetails.qty,
        leverage: leverage,
        side: apiSide as 1 | 2 | 3 | 4,
        openType: 1 as 1 | 2, // 1: isolated, 2: cross
        orderType: apiOrderType as 1 | 2 | 3 | 4 | 5,
        ...(orderType === 'limit' && { price: parseFloat(price) })
      };

      console.log('Placing order:', orderRequest);
      const result = await apiClient.createOrder(orderRequest);
      
      setSuccess(`Order placed successfully! Order ID: ${result.orderId}`);
      
      // Reset form
      setAmount('');
      setPercentage(0);
      
      // Refresh data after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (err: any) {
      console.error('Order placement error:', err);
      // Enhanced error for code 1002
      if (err.message.includes('1002') || err.message.includes('不允许下单')) {
        setError('Current contract does not allow placing orders. Please select a different symbol or check MEXC status.');
      } else {
        setError(err.message || 'Failed to place order. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Order Panel
        </h3>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Symbol Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Symbol
          </label>
          <select
            value={selectedSymbol || ''}
            onChange={(e) => onSymbolSelect(e.target.value)}
            disabled={isLoadingSymbols}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">
              {isLoadingSymbols ? 'Loading symbols...' : 'Select a symbol'}
            </option>
            {availableSymbols.length > 0 ? (
              availableSymbols.map(symbol => (
                <option key={symbol} value={symbol}>
                  {symbol}
                </option>
              ))
            ) : (
              <option disabled>No symbols available</option>
            )}
          </select>
          {availableSymbols.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {availableSymbols.length} trading pairs available
            </p>
          )}
        </div>

        {selectedSymbol && (isFetchingPrice || currentPrice) && (
          <>
            {/* Current Price Display */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Current Price</span>
                {isFetchingPrice ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>
                  </div>
                ) : (
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    ${currentPrice?.toFixed(2) || 'N/A'}
                  </span>
                )}
              </div>
            </div>

            {currentPrice && !isFetchingPrice && (
              <>
                {/* Available Balance */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Available Balance</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        ${availableBalance.toFixed(2)} USDT
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Max Tradeable ({leverage}x)</span>
                      <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                        ${maxTradeableAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Order Type
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setOrderType('market')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        orderType === 'market'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      Market
                    </button>
                    <button
                      onClick={() => setOrderType('limit')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        orderType === 'limit'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      Limit
                    </button>
                  </div>
                </div>

                {/* Side Selection */}
                {/* Side Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Side
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSide('long')}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        side === 'long'
                          ? 'bg-success-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      <TrendingUp className="w-4 h-4" />
                      <span>Long</span>
                    </button>
                    <button
                      onClick={() => setSide('short')}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        side === 'short'
                          ? 'bg-danger-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      <TrendingDown className="w-4 h-4" />
                      <span>Short</span>
                    </button>
                  </div>
                </div>

                {/* Amount Mode Toggle & Input */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Amount
                    </label>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => setAmountMode('usd')}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                          amountMode === 'usd'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <DollarSign className="w-3 h-3" />
                        <span>USD</span>
                      </button>
                      <button
                        onClick={() => setAmountMode('qty')}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                          amountMode === 'qty'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <Hash className="w-3 h-3" />
                        <span>QTY</span>
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      placeholder={amountMode === 'usd' ? 'Enter USD amount' : 'Enter quantity'}
                      step={amountMode === 'usd' ? '0.01' : '0.000001'}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {amount && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">
                        {amountMode === 'usd'
                          ? `≈ ${convertAmount.qty.toFixed(6)} ${selectedSymbol.replace('USDT', '')}`
                          : `≈ $${convertAmount.usd.toFixed(2)}`
                        }
                      </div>
                    )}
                  </div>
                </div>

                {/* Percentage Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Use {percentage.toFixed(0)}% of available
                    </label>
                  </div>

                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={percentage}
                    onChange={(e) => handlePercentageChange(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  />

                  {/* Quick Select Buttons */}
                  <div className="flex justify-between mt-2 space-x-2">
                    {[25, 50, 75, 100].map(pct => (
                      <button
                        key={pct}
                        onClick={() => handlePercentageChange(pct)}
                        className="flex-1 px-2 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price (for limit orders) */}
                {orderType === 'limit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Limit Price
                    </label>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Enter limit price"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                )}

                <div>
                  {/* Leverage */}
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leverage: {leverage}x
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={leverage}
                    onChange={(e) => setLeverage(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>1x</span>
                    <span>50x</span>
                    <span>100x</span>
                  </div>
                </div>

                {/* Order Details */}
                {orderDetails && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Order Summary
                    </h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Order Value</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        ${orderDetails.notionalValue.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Quantity</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {orderDetails.qty.toFixed(6)} {selectedSymbol.replace('USDT', '')}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Required Margin</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        ${orderDetails.requiredMargin.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Est. Liq. Price</span>
                      <span className="font-medium text-danger-600">
                        ${orderDetails.liquidationPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="flex items-start space-x-2 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-danger-800 dark:text-danger-200">{error}</p>
                  </div>
                )}
              
                {/* Contract Warning */}
                {contractStatus === 'invalid' && contractError && (
                  <div className="flex items-start space-x-2 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-warning-800 dark:text-warning-200">{contractError}</p>
                  </div>
                )}

                {/* Success Message */}
                {success && (
                  <div className="flex items-start space-x-2 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-success-800 dark:text-success-200">{success}</p>
                  </div>
                )}

                {/* Place Order Button */}
                <button
                  onClick={handlePlaceOrder}
                  disabled={!finalFormValid || isSubmitting || contractStatus !== 'valid'}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-3 font-medium rounded-lg transition-colors ${
                    side === 'long'
                      ? 'bg-success-600 hover:bg-success-700 disabled:bg-gray-400'
                      : 'bg-danger-600 hover:bg-danger-700 disabled:bg-gray-400'
                  } text-white disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Placing Order...</span>
                    </>
                  ) : (
                    <>
                      {side === 'long' ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                      <span>Place {side.charAt(0).toUpperCase() + side.slice(1)} Order</span>
                    </>
                  )}
                </button>

                {/* Validation Messages */}
                {!isFormValid && amount && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {!amount && "Enter an amount"}
                    {orderType === 'limit' && !price && "Enter a limit price"}
                    {orderDetails && orderDetails.requiredMargin > availableBalance &&
                      "Insufficient balance for this order"}
                  </div>
                )}
              </>
            )}

            {isFetchingPrice && (
              <div className="text-center py-8">
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Loading price data for {selectedSymbol}...
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {!selectedSymbol && (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              Select a symbol to start trading
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
